/**
 * @file 全局异步下载任务中心
 * @module composables/useDownloadManager
 * @description 统一维护并发下载任务、非阻塞面板状态和完成记录。
 */
import { computed, reactive, ref, type ComputedRef, type Ref } from 'vue'
import type {
  DownloadProgressCallback,
  DownloadProgressState
} from '@/utils/download'

/** 下载任务执行状态。 */
export type DownloadTaskStatus = 'queued' | 'running' | 'completed' | 'failed'

/** 下载任务展示数据。 */
export interface DownloadTask {
  /** 下载任务唯一标识。 */
  id: string
  /** 下载任务标题。 */
  title: string
  /** 下载任务执行状态。 */
  status: DownloadTaskStatus
  /** 下载任务最新进度。 */
  progress: DownloadProgressState
  /** 下载失败时的用户可读错误。 */
  error?: string
  /** 下载任务创建时间戳。 */
  createdAt: number
  /** 下载任务完成或失败时间戳。 */
  finishedAt?: number
}

/** 创建下载任务所需配置。 */
export interface DownloadTaskOptions<TResult> {
  /** 下载任务标题。 */
  title: string
  /** 实际下载逻辑。 */
  run: (onProgress: DownloadProgressCallback) => Promise<TResult>
  /** 下载成功后的面板文案或文案生成器。 */
  successMessage?: string | ((result: TResult) => string)
  /** 下载错误文案格式化方法。 */
  formatError?: (error: unknown) => string
  /** 下载成功后的非阻塞回调。 */
  onSuccess?: (result: TResult) => void | Promise<void>
  /** 下载失败后的非阻塞回调。 */
  onError?: (error: unknown) => void | Promise<void>
}

/** 已创建下载任务的句柄。 */
export interface DownloadTaskHandle {
  /** 下载任务唯一标识。 */
  id: string
  /** 下载任务结束后必定完成的 Promise。 */
  completion: Promise<void>
}

/** 全局下载任务中心控制器。 */
interface DownloadManagerControls {
  /** 下载任务面板是否显示。 */
  downloadCenterVisible: Ref<boolean>
  /** 全部下载任务，最新任务位于最前。 */
  downloadTasks: Ref<DownloadTask[]>
  /** 正在排队或执行的下载任务数。 */
  activeDownloadCount: ComputedRef<number>
  /** 是否存在可清理的完成或失败任务。 */
  hasFinishedDownloads: ComputedRef<boolean>
  /** 打开下载任务面板。 */
  openDownloadCenter: () => void
  /** 关闭下载任务面板但不终止任务。 */
  closeDownloadCenter: () => void
  /** 切换下载任务面板显隐状态但不终止任务。 */
  toggleDownloadCenter: () => void
  /** 清理完成和失败的下载记录。 */
  clearFinishedDownloads: () => void
}

/** 最多保留的历史下载记录数。 */
const MAX_FINISHED_DOWNLOADS = 30

/** 下载任务面板是否显示。 */
const downloadCenterVisible = ref(false)

/** 全局下载任务列表。 */
const downloadTasks = ref<DownloadTask[]>([])

/** 下载任务递增序号。 */
let downloadTaskSequence = 0

/** 正在排队或执行的下载任务数。 */
const activeDownloadCount = computed(
  () =>
    downloadTasks.value.filter(
      (task) => task.status === 'queued' || task.status === 'running'
    ).length
)

/** 是否存在可清理的完成或失败任务。 */
const hasFinishedDownloads = computed(() =>
  downloadTasks.value.some(
    (task) => task.status === 'completed' || task.status === 'failed'
  )
)

/**
 * 限制下载进度百分比。
 * @param percent 原始下载进度。
 * @returns 0 到 100 的整数进度。
 */
const clampDownloadPercent = (percent: number): number =>
  Math.max(0, Math.min(100, Math.round(percent)))

/**
 * 生成默认下载错误文案。
 * @param error 下载任务抛出的异常。
 * @returns 用户可读错误文案。
 */
const formatDefaultDownloadError = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim()
  }
  return '下载失败'
}

/**
 * 获取下载成功文案。
 * @param option 下载成功文案配置。
 * @param result 下载任务返回值。
 * @returns 下载中心展示的完成文案。
 */
const resolveSuccessMessage = <TResult>(
  option: DownloadTaskOptions<TResult>['successMessage'],
  result: TResult
): string => {
  if (typeof option === 'function') {
    return option(result)
  }
  return option || '下载完成'
}

/**
 * 裁剪过长的下载历史记录。
 * @returns 保留所有活动任务和最近完成记录。
 */
const trimDownloadHistory = (): void => {
  let finishedCount = 0
  downloadTasks.value = downloadTasks.value.filter((task) => {
    if (task.status === 'queued' || task.status === 'running') {
      return true
    }
    finishedCount += 1
    return finishedCount <= MAX_FINISHED_DOWNLOADS
  })
}

/**
 * 安全执行下载结果回调。
 * @param callback 下载成功或失败后的回调。
 * @param payload 传给回调的结果或异常。
 * @returns 回调完成后结束；回调异常仅记录日志。
 */
const invokeDownloadCallback = async <TPayload>(
  callback: ((payload: TPayload) => void | Promise<void>) | undefined,
  payload: TPayload
): Promise<void> => {
  if (!callback) return
  try {
    await callback(payload)
  } catch (error) {
    console.error('[download-manager] 下载结果回调执行失败:', error)
  }
}

/**
 * 执行单个下载任务。
 * @param task 当前下载任务。
 * @param options 下载任务配置。
 * @returns 下载成功或失败状态写入任务中心后结束。
 */
const runDownloadTask = async <TResult>(
  task: DownloadTask,
  options: DownloadTaskOptions<TResult>
): Promise<void> => {
  task.status = 'running'
  try {
    const result = await options.run((progress) => {
      task.progress = {
        ...progress,
        percent: clampDownloadPercent(progress.percent)
      }
    })
    task.status = 'completed'
    task.finishedAt = Date.now()
    task.progress = {
      ...task.progress,
      phase: 'saving',
      percent: 100,
      message: resolveSuccessMessage(options.successMessage, result)
    }
    await invokeDownloadCallback(options.onSuccess, result)
  } catch (error) {
    let message = formatDefaultDownloadError(error)
    if (options.formatError) {
      try {
        message = options.formatError(error) || message
      } catch (formatError) {
        console.error('[download-manager] 下载错误文案格式化失败:', formatError)
      }
    }
    task.status = 'failed'
    task.finishedAt = Date.now()
    task.error = message
    task.progress = {
      ...task.progress,
      message
    }
    await invokeDownloadCallback(options.onError, error)
  } finally {
    trimDownloadHistory()
  }
}

/** 打开下载任务面板。 */
const openDownloadCenter = (): void => {
  downloadCenterVisible.value = true
}

/** 关闭下载任务面板但不终止后台任务。 */
const closeDownloadCenter = (): void => {
  downloadCenterVisible.value = false
}

/** 切换下载任务面板显隐状态但不终止后台任务。 */
const toggleDownloadCenter = (): void => {
  downloadCenterVisible.value = !downloadCenterVisible.value
}

/** 清理已完成和失败的下载记录。 */
const clearFinishedDownloads = (): void => {
  downloadTasks.value = downloadTasks.value.filter(
    (task) => task.status === 'queued' || task.status === 'running'
  )
}

/**
 * 创建并异步启动下载任务。
 * @param options 下载标题、执行逻辑和结果回调。
 * @returns 可用于测试或后续扩展的任务标识与完成 Promise。
 */
export const startDownloadTask = <TResult>(
  options: DownloadTaskOptions<TResult>
): DownloadTaskHandle => {
  downloadTaskSequence += 1
  const task = reactive<DownloadTask>({
    id: `download-${Date.now()}-${downloadTaskSequence}`,
    title: options.title,
    status: 'queued',
    progress: {
      phase: 'preparing',
      percent: 0,
      message: '等待开始下载'
    },
    createdAt: Date.now()
  })
  downloadTasks.value.unshift(task)
  openDownloadCenter()

  const completion = new Promise<void>((resolve) => {
    globalThis.setTimeout(() => {
      void runDownloadTask(task, options).finally(resolve)
    }, 0)
  })

  return { id: task.id, completion }
}

/**
 * 使用全局下载任务中心。
 * @returns 下载任务列表、活动数量和面板控制方法。
 */
export const useDownloadManager = (): DownloadManagerControls => ({
  downloadCenterVisible,
  downloadTasks,
  activeDownloadCount,
  hasFinishedDownloads,
  openDownloadCenter,
  closeDownloadCenter,
  toggleDownloadCenter,
  clearFinishedDownloads
})
